<template>
  <div class="email-properties">
    <card
      title="Email Properties"
      sub-title="Send a shortlist of suitable properties to a prospective buyer."
    >
      <form @submit.prevent="sendEmail">
        <fieldset class="form-group">
          <label
            >Buyer Email*
            <info-box>
              <ul>
                <li>Use commas to enter multiple email addresses</li>
              </ul>
            </info-box>
          </label>

          <div class="form-control d-flex">
            <ul class="input-tags">
              <li
                v-for="(email, i) in emailList"
                :key="email + i"
                @click="removeEmail(i)"
              >
                {{ email }}
                <fa :icon="['fas', 'times']" />
              </li>
            </ul>

            <input
              type="email"
              class="input-plain"
              v-model="email"
              placeholder="user@domain.com"
              :required="emailList.length === 0"
              ref="emailInput"
              @keydown="onTagKey"
            />
          </div>
        </fieldset>
        <fieldset class="form-group">
          <label>Sender Email*</label>
          <input
            type="email"
            class="form-control"
            v-model="senderEmail"
            placeholder="user@domain.com"
            required
          />
        </fieldset>
        <fieldset class="form-group">
          <label>Personalised Email Message</label>
          <ck-editor v-model="message" height="180px"></ck-editor>
        </fieldset>
        <fieldset class="form-group">
          <label>Property Shortlist*</label>
          <input-multi-select
            v-model="selectedProperties"
            :options="propertyOptions"
            label="Properties"
          ></input-multi-select>
        </fieldset>
        <fieldset class="form-group">
          <a :href="exampleFavouritesUrl" target="_blank"
            >Shortlist Example Link</a
          >
          - Use this link to review property shortlist.
        </fieldset>
        <button class="btn btn-primary mb-3" :disabled="isSubmitting">
          {{ isSubmitting ? "Sending..." : "Send Email" }}
        </button>
        <transition name="fade">
          <fieldset v-show="isSubmitted" class="form-group">
            <div
              v-for="message in emailMessages"
              :key="message"
              v-html="message"
            ></div>
          </fieldset>
        </transition>
      </form>
    </card>
  </div>
</template>

<script>
import ApiService from "../common/api.service";
import Card from "../components/Cards/Card";
import CkEditor from "../components/CKEditor";
import InputMultiSelect from "../components/InputMultiSelect";
import InfoBox from "@/components/InfoBox";
import { mapActions, mapGetters } from "vuex";
export default {
  components: { Card, CkEditor, InfoBox, InputMultiSelect },

  data() {
    return {
      email: "",
      emailList: [],
      emailMessages: [],
      isSubmitted: false,
      isSubmitting: false,
      message: "",
      selectedProperties: [],
      senderEmail: null,
      packages: []
    };
  },

  computed: {
    ...mapGetters(["currentUser", "structuredProperties"]),

    propertyOptions() {
      let properties = this.structuredProperties.map(property => {
        if (
          property.livingOptionId &&
          property.subsectionId &&
          property.status === "Available"
        ) {
          return {
            label: `${property.label} - ${property.subsection.label} - ${property.livingOption.label}`,
            value: property._id
          };
        }
      });

      let _packages = this.packages.map(_package => {
        if (
          _package.active &&
          _package.property.livingOptionId &&
          _package.property.subsectionId
        ) {
          return {
            label: `Package: ${_package.dwelling.label} - ${_package.property.label}`,
            value: _package._id
          };
        }
      });

      return [...properties, ..._packages].filter(property => property);
    },

    favouritesUrl() {
      return `${process.env.VUE_APP_FAVOURITES_HOST}?r=0&p=${this.selectedProperties}&e=${this.email}&ref=tool`;
    },

    exampleFavouritesUrl() {
      return `${process.env.VUE_APP_FAVOURITES_HOST}?r=1&p=${this.selectedProperties}&ref=tool`;
    }
  },

  created() {
    this.$store.dispatch("FETCH_STRUCTURED_PROPERTIES");
    this.axios.get("packages/lot-available").then(res => {
      this.packages = res.data;
    });
  },

  methods: {
    sendEmail() {
      this.isSubmitting = true;

      const emails = this.emailList.length ? this.emailList : [this.email];

      emails.forEach(email => {
        ApiService.post("favourites/shortlist", {
          email: email,
          senderEmail: this.senderEmail,
          propertyIds: this.selectedProperties,
          message: this.message
        })
          .then(() => {
            this.emailMessages.push(
              `${new Date().toLocaleString()} Email queued for delivery to <strong>${email}</strong>`
            );
          })
          .catch(err => {
            this.emailMessages.push(
              `${new Date().toLocaleString()} Error sending email to <strong>${email}</strong>`
            );
          })
          .finally(() => {
            this.lastEmail = email;
            this.isSubmitting = false;
            this.isSubmitted = true;
          });
      });
    },

    onTagKey(e) {
      if ((e.key === "Enter" || e.key === ",") && this.email) {
        e.preventDefault();

        this.emailList.push(this.email);
        this.email = "";
      }

      if (
        e.key === "Backspace" &&
        this.email.length === 0 &&
        this.emailList.length > 0
      ) {
        this.emailList.splice(-1);
      }
    },

    removeEmail(index) {
      this.emailList.splice(index, 1);
    }
  },

  mounted() {
    this.senderEmail = this.currentUser.email;
    this.message = process.env.VUE_APP_EMAIL_MESSAGE
      ? process.env.VUE_APP_EMAIL_MESSAGE
      : `<p>Dear &lt;Customer Name&gt;,</p><p>Thank you for your interest in purchasing a property at ${process.env.VUE_APP_PROJECT.charAt(
          0
        ).toUpperCase() +
          process.env.VUE_APP_PROJECT.slice(
            1
          )}. Please find below a link to view additional information about the property that we discussed today.</p><p>Please get in touch on ${this
          .currentUser.phone || "[PHONE]"} or ${this.currentUser.email ||
          "[EMAIL]"} if you have any further questions.</p><p>Kind regards,<br />${this
          .currentUser.name || "[NAME]"}</p>`;
    this.$refs.emailInput.focus();
  }
};
</script>

<style lang="scss">
.email-properties {
  .form-group label {
    font-weight: 300;
  }
}

.email-messagess {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
}

.input-tags {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    background: rgba(44, 128, 255, 1);
    border-radius: 3px;
    color: #fff;
    cursor: pointer;
    margin-right: 5px;
    padding: 0 5px;
    position: relative;

    svg {
      color: #222;
      left: 50%;
      opacity: 0;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &:hover {
      background: rgba(44, 128, 255, 0.5);
      filter: grayscale(50%);

      svg {
        opacity: 1;
      }
    }
  }
}

.input-plain {
  border: none;
  box-shadow: none;
  padding: 0;
  width: 100%;
}
</style>
